<script lang="ts">
	//UI
	import {
		OutlineButton,
		SlotSelect,
		HubProfileTextInput,
		DefaultButton,
	} from "@hitit/new_ui";
	import Message from "./Message.svelte";
	import CancelButton from "./CancelButton.svelte";
	import ProfileInputFrame from "./ProfileInputFrame.svelte";
	import ProfileViewer from "./ProfileViewer.svelte";
	//Utils
	import type { PhoneNumber } from "libphonenumber-js";
	import { getCountries, getCountryCallingCode } from "libphonenumber-js";
	import {
		addSpacer,
		getNationalPhonenumber,
	} from "@hitit/hub/src/functions/utils/phone";
	import { t, api } from "@hitit/hub/src/stores";
	import parsePhone from "libphonenumber-js";
	import type { ProfileData } from "@hitit/api";
	import { getInternationalPhonenumber } from "../../../functions/utils/phone";

	export let profile: ProfileData;

	//constants
	const countryNames: Record<string, string> = $t("global.countries", {
		returnObjects: true,
	});

	//variables
	let phonenumberCode = profile.cellphoneCountryCode;
	let phonenumber = "";

	let loading = false;
	let gettingCode = false;
	let editmode = false;

	let codeEntered = "";
	let codeExpected = "";
	//Error
	let codeError = "";
	let getCodeError = "";

	let parsed: PhoneNumber | undefined = undefined;

	$: countries = getCountries().map((v) => ({
		label: `${countryNames[v.toLowerCase()]} +${getCountryCallingCode(v)}`,
		value: getCountryCallingCode(v) as string,
		display: `+${getCountryCallingCode(v)}`,
	}));

	function resetAll() {
		codeEntered = "";
		codeExpected = "";
		getCodeError = "";
		codeError = "";
		phonenumber = "";
		phonenumberCode = profile.cellphoneCountryCode;
		editmode = false;
	}

	//functions
	async function getCode() {
		try {
			gettingCode = true;
			const nationalPhoneNumber = getNationalPhonenumber(
				phonenumber,
				phonenumberCode
			);
			const tmpParsed = parsePhone(
				`+${phonenumberCode} ${nationalPhoneNumber}`
			);
			if (tmpParsed === undefined || !tmpParsed.isValid()) {
				getCodeError = "hub.profile.phonenumber_error";
				return;
			}
			parsed = tmpParsed;
			const r = await $api.requestMobileCode(
				parsed.nationalNumber as string,
				parsed.countryCallingCode as string
			);
			codeExpected = r.data.response;
			getCodeError = "";
		} catch {
			getCodeError = "hub.profile.getCodeError";
		} finally {
			gettingCode = false;
		}
	}

	function onInput(e: Event) {
		const target = e.currentTarget as HTMLInputElement;
		const { value } = target;
		const originalCaret = target.selectionEnd;
		const originalGap = value.length - (originalCaret || 0);
		const replace = value
			.replace(/-/g, "") //"-"제거
			.replace(/[^0-9]/g, ""); //숫자를제거한 나머지 모두 제거
		const newValue = addSpacer(replace); //다시 addSpacer
		target.value = newValue;
		target.focus();

		//캐럿을 따로 설정해주지 않으면, target.value = newValue로 인해서 캐럿이 항상 마지막으로 이동한다.
		const newPosition = newValue.length - originalGap;
		target.setSelectionRange(newPosition, newPosition);

		phonenumber = replace;
		codeExpected = "";
		getCodeError = "";
	}

	async function compareHandler() {
		try {
			if (codeEntered !== codeExpected) {
				throw new Error("hub.profile.phonenumber_code_error");
			}
			loading = true;
			codeExpected = "";
			getCodeError = "";
			codeError = "";

			const newProfile = Object.assign({}, profile, {
				cellphone: getInternationalPhonenumber(phonenumber, phonenumberCode),
				cellphoneCountryCode: phonenumberCode,
				cellphoneVerified: true,
			});
			await $api.updateProfile(newProfile);
			profile = newProfile;
			resetAll();
		} catch (e) {
			codeError = "hub.profile.phonenumber_code_error";
		} finally {
			loading = false;
		}
	}

	$: saveButtonDisabled =
		codeError || getCodeError || codeEntered === "" || codeExpected === ""
			? true
			: false;
	$: backgroundColor = saveButtonDisabled ? "#a5c1f9" : "#6b93f3";
</script>

<form
	class:editmode={editmode}
	class:verify={codeExpected !== ""}
	on:submit|preventDefault={() => {}}
>
	{#if !editmode}
		<div class="container">
			<ProfileViewer
				on:click={() => {
					editmode = true;
				}}
				name={$t("hub.profile.phonenumber")}
				value={addSpacer(
					getNationalPhonenumber(
						profile.cellphone,
						profile.cellphoneCountryCode
					)
				)}
			/>
		</div>
	{:else}
		<div class="container">
			<div class="messageFrame">
				<ProfileInputFrame name={$t("hub.profile.phonenumber")}>
					<div class="select-box">
						<SlotSelect
							bind:value={phonenumberCode}
							let:displayValue
							let:noValue
							items={countries}
							placeholder={"+82"}
						>
							<span class:placeholder={noValue}>
								{noValue ? "+82" : displayValue}
							</span>
						</SlotSelect>
					</div>
					<HubProfileTextInput
						on:input={onInput}
						id="phonenumber"
						error={getCodeError !== ""}
						clearCallback={() => {
							(phonenumber = ""), (codeExpected = "");
						}}
						value={phonenumber}
					/>
					<div class="send-box">
						<OutlineButton
							on:click={getCode}
							disabled={gettingCode}
							isLoading={gettingCode}
							noneBordercolor
							nonePadding
							fitmode
						>
							<span
								>{codeExpected === ""
									? $t("global.verify")
									: $t("hub.profile.resend")}</span
							>
						</OutlineButton>
					</div>
				</ProfileInputFrame>
				<Message messageTrnaslation={getCodeError} error />
			</div>
			<div class="messageFrame">
				<ProfileInputFrame>
					<HubProfileTextInput
						id="code"
						placeholder={$t("hub.profile.phonenumber_code")}
						on:input={() => (codeError = "")}
						bind:value={codeEntered}
						error={codeError !== ""}
						clearCallback={() => (codeEntered = "")}
					/>
				</ProfileInputFrame>
				<Message
					messageTrnaslation={codeError
						? codeError
						: codeExpected === ""
						? ""
						: "hub.profile.send_code_success"}
					error={Boolean(codeError)}
				/>
			</div>
		</div>
		<div class="button-container">
			<div class="button-box done">
				<DefaultButton
					nonePadding
					isLoading={loading}
					background={backgroundColor}
					disabled={saveButtonDisabled}
					fitmode
					on:click={compareHandler}
				>
					<span class="white">{$t("global.save")}</span>
				</DefaultButton>
			</div>
		</div>
		<div class="cancelbox">
			<CancelButton on:click={() => resetAll()} />
		</div>
	{/if}
</form>

<style lang="scss">
	@import "../../../variables.scss";
	div {
		box-sizing: border-box;
	}

	form {
		display: flex;
		flex-direction: column;
		position: relative;
		padding: 0 20px;
		gap: 10px;

		.cancelbox {
			position: absolute;
			top: 10px;
			right: 10px;
		}

		&.editmode {
			background-color: #e6e7ed;
			padding: 20px 20px;
		}

		.messageFrame {
			display: flex;
			flex-grow: 1;
			flex-direction: column;
			gap: 6px;
		}

		.container {
			display: flex;
			flex-grow: 1;
			flex-direction: column;
			gap: 10px;
		}
		.select-box {
			height: 31px;
			flex-basis: 60px;
			min-width: 60px;
			span {
				color: #46474b;
				padding-left: 10px;
				font-weight: 300;
				&.placeholder {
					color: #bcbfc3;
				}
			}
		}

		.button-box {
			flex-grow: 1;
			height: 32px;

			&.done {
				.white {
					display: flex;
					justify-content: center;
					align-items: center;
					width: 100%;
					height: 100%;
					color: white;
					font-size: 1rem;
				}
			}
		}
		.send-box {
			height: 32px;
			flex-basis: 60px;
			min-width: 60px;
			border: 1px solid #6d6f75;
			border-radius: 5px;
			span {
				color: #6d6f76;
				font-weight: 500;
				@include heightRelativeToFontSize(16);
			}
		}
	}

	@include DesktopSize {
		form {
			flex-direction: row;
			position: relative;
			padding: 0 40px;
			gap: 20px;

			&.editmode {
				padding: 20px 40px;
			}

			.cancelbox {
				position: absolute;
				top: 20px;
				right: 20px;
			}

			&.verify {
				background-color: #e6e7ed;
				padding: 20px 40px;
			}

			.messageFrame {
				gap: 10px;
			}

			.container {
				flex-direction: column;
				gap: 20px;
			}

			.button-container {
				display: flex;
				.button-box {
					min-width: 150px;
					flex-basis: 150px;
					flex-grow: 0;
					box-sizing: border-box;
					height: 40px;
	
					&.done {
						margin-top: auto;
						.white {
							font-size: 1.25rem;
						}
					}
				}
			}

			.select-box {
				height: 40px;
				flex-basis: 100px;
				min-width: 100px;

				span {
					font-size: 1.25rem;
					font-weight: 500;
					&.placeholder {
						color: #bcbfc3;
					}
				}
			}

			.send-box {
				min-width: 100px;
				height: 40px;
				span {					
					height: auto;
					padding-bottom: 1px;
					@include heightRelativeToFontSize(20);
				}
			}
		}
	}
</style>
